import { createBrowserRouter, RouterProvider, Outlet } from "react-router-dom";
import { lazy, Suspense } from "react";
import CommandCentreContent from "@/pages/digiGuardianGoa/CommandCentreContent";
import DepartmentDashboardContent from "@/pages/digiGuardianGoa/DepartmentDashboardContent";
import HODDashboardContent from "@/pages/digiGuardianGoa/HODDashboardContent";

const App = lazy(() => import("../App"));
const SignUp = lazy(() => import("../pages/auth/SignUp"));
const ProtectedRoute = lazy(() => import("../protectedRoutes/ProtectedRoute"));
const PrivacyPolicyPage = lazy(() => import("../pages/privacyPolicy/PrivacyPolicyPage.jsx"));
const SetupAccount = lazy(() => import("../pages/auth/SetupAccount"));
const AssetDeshboard1 = lazy(() => import("@/pages/assetManagement/AssetDeshboard1.jsx"));
const Clients = lazy(() => import("../pages/clients/Clients"));
const Fields = lazy(() => import("../pages/Fields/Fields"));
const MapView = lazy(() => import("../pages/mapView/mapView"));
const Settings = lazy(() => import("../pages/settings/Settings"));
const Dashboard = lazy(() => import("../pages/dashboard/Dashboard.jsx"));
const Blocks = lazy(() => import("./../pages/blocks/Blocks"));
const Members = lazy(() => import("./../pages/members/Members"));
const Permissions = lazy(() => import("../pages/permissions/Permissions"));
const Roles = lazy(() => import("../pages/roles/Roles"));
const Login = lazy(() => import("../pages/auth/Login"));
const UpdatePassword = lazy(() => import("../pages/auth/UpdatePassword"));
const Records = lazy(() => import("../pages/records/Records"));
const RecordMap = lazy(() => import("../pages/records/RecordMap"));
const ForgotPassword = lazy(() => import("../pages/auth/ForgotPassword"));
const Profile = lazy(() => import("../pages/profile/Profile"));
const Loadings = lazy(() => import("../components/loading/Loading"));
const LiveLocation = lazy(() => import("../pages/records/LiveLocation"));
const NotFoundPage = lazy(() => import("../components/notFoundPage/NotFoundPage"));
const CustomMap = lazy(() => import("../pages/records/CustomMap"));
const Booking = lazy(() => import("../pages/booking/booking"));
const ThankYouCard = lazy(() => import("../components/thankYouCard/ThankYouCard"));
const VehiclesHistory = lazy(() => import("../pages/records/VehiclesHistory"));
const PipelineMap = lazy(() => import("../pages/MapPage/MapPage.jsx"));
const WarehouseMaster = lazy(() => import("../pages/warehouse/Warehouse.jsx"));
const Inventory = lazy(() => import("../pages/Inventory/Inventory.jsx"));
const InboundGoods = lazy(() => import("../pages/inbound/Inboundgoods.jsx"));
const OutboundDispatch = lazy(() => import("../pages/outbound/OutboundGoods.jsx"));
const OrderManagement = lazy(() => import("../pages/ordermanagement/Ordermanagement.jsx"));
const StockMovement = lazy(() => import("../pages/stockMovement/StockMovement.jsx"));
const InventoryAuditReport = lazy(() => import("../pages/inventoryAuditReport/InventoryAuditReport.jsx"));
const DigitalTwinModule = lazy(() => import("../components/MapView/DigitalTwinModule.jsx"));

import WaterOperationsDashboard from "@/components/waterOperation/WaterOperationsDashboard.jsx";
import StationPerformanceDashboard from "@/components/waterOperation/StationPerformanceDashboard.jsx";
import ComplianceDashboard from "@/components/waterOperation/ComplianceDashboard.jsx";
import IllegalHeatmapDashboard from "@/components/waterOperation/IllegalHeatmapDashboard.jsx";
import OrderComplianceDashboard from "@/components/waterOperation/OrderComplianceDashboard.jsx";
import OperationsReport from "@/pages/assetFlowReports/OperationsReport.jsx";
import EnergyEfficiencyDashboard from "@/pages/assetFlowReports/EnergyEfficiencyDashboard.jsx";
import NRWDashboard from "@/pages/assetFlowReports/NRWDashboard.jsx";
import AssetHealth from "@/pages/assetFlowReports/AssetHealth.jsx";
import ManagementSummary from "@/pages/assetFlowReports/ManagementSummary.jsx";
import FlowReport from "@/pages/assetFlowReports/FlowReport.jsx";
import PredictiveAnalytics from "@/pages/assetFlowReports/PredictiveAnalytics.jsx";
import AssetReports from "@/pages/assetReports/AssetReports.jsx";
import AssetMaintenance from "@/pages/assetReports/AssetMaintenance.jsx";
import EnergyEfficiency from "@/pages/assetFlowReports/EnergyEfficiency.jsx";
const FinancialAssetDashboard = lazy(() => import("@/pages/assetManagement/FinancialAssetDashboard.jsx"));
const AssetDashboard = lazy(() => import("@/pages/assetManagement/PerformanceAndHealth.jsx"));
import WarehouseAnalytics from "@/components/warehouse/WarehouseAnalytics.jsx";
import NoAccess from "../pages/NoAccess";
import TankerFillingFlow from "@/components/tanker-filling/TankerFillingFlow";

const Loading = () => <Loadings />;

const AppRouter = () => {
  const Router = createBrowserRouter([
    {
      path: "/",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<Loading />}>
            <App />
          </Suspense>
        </ProtectedRoute>
      ),
      children: [
        {
          path: "",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <HODDashboardContent />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "/no-access",
          element: <NoAccess />,
        },
        {
          path: "water-operations-reports",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <WaterOperationsDashboard />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "station-performance-reports",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <StationPerformanceDashboard />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "compliance-reports",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <ComplianceDashboard />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "suspected-illegal-filling",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <IllegalHeatmapDashboard />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "order-compliance-reports",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <OrderComplianceDashboard />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "asset-flow-operations",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <OperationsReport />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "energy-efficiency",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <EnergyEfficiencyDashboard />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "nrw-analytics",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <NRWDashboard />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "asset-reports",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <AssetReports />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "asset-health",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <AssetHealth />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "predictive-analytics",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <PredictiveAnalytics />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "management-summary",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <ManagementSummary />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "financial-reports",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <FinancialAssetDashboard />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "flow-report",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <FlowReport />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "asset-maintenance",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <AssetMaintenance />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "energy-efficiency-report",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <EnergyEfficiency />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "performance-reports",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <AssetDashboard />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "warehouse",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <WarehouseAnalytics />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "users",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <Members />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "blocks",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <Blocks />
              </Suspense>
            </ProtectedRoute>
          ),
          children: [
            {
              path: ":blockId",
              element: (
                <ProtectedRoute>
                  <Suspense fallback={<Loading />}>
                    <Fields />
                  </Suspense>
                </ProtectedRoute>
              ),
            },
            {
              path: ":blockId/records",
              element: (
                <ProtectedRoute>
                  <Suspense fallback={<Loading />}>
                    <Records />
                  </Suspense>
                </ProtectedRoute>
              ),
            },
            {
              path: ":blockId/map",
              element: (
                <ProtectedRoute>
                  <Suspense fallback={<Loading />}>
                    <RecordMap />
                  </Suspense>
                </ProtectedRoute>
              ),
            },
            {
              path: ":blockId/custom-map/:recordId",
              element: (
                <ProtectedRoute>
                  <Suspense fallback={<Loading />}>
                    <CustomMap />
                  </Suspense>
                </ProtectedRoute>
              ),
            },
            {
              path: ":blockId/custom-map",
              element: (
                <ProtectedRoute>
                  <Suspense fallback={<Loading />}>
                    <CustomMap />
                  </Suspense>
                </ProtectedRoute>
              ),
            },
            {
              path: ":blockId/:recordId/vehicle-history",
              element: (
                <ProtectedRoute>
                  <Suspense fallback={<Loading />}>
                    <VehiclesHistory />
                  </Suspense>
                </ProtectedRoute>
              ),
            },
            {
              path: ":blockId/:recordId/live-location",
              element: (
                <ProtectedRoute>
                  <Suspense fallback={<Loading />}>
                    <LiveLocation />
                  </Suspense>
                </ProtectedRoute>
              ),
            },
          ],
        },
        {
          path: "profile",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <Profile />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "permissions",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <Permissions />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "roles",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <Roles />
              </Suspense>
            </ProtectedRoute>
          ),
        },

        {
          path: "warehouse-management",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <Outlet />
              </Suspense>
            </ProtectedRoute>
          ),
          children: [
            {
              path: "warehouse-master",
              element: (
                <ProtectedRoute>
                  <Suspense fallback={<Loading />}>
                    <WarehouseMaster />
                  </Suspense>
                </ProtectedRoute>
              ),
            },
            {
              path: "inventory",
              element: (
                <ProtectedRoute>
                  <Suspense fallback={<Loading />}>
                    <Inventory />
                  </Suspense>
                </ProtectedRoute>
              ),
            },
            {
              path: "inbound-goods",
              element: (
                <ProtectedRoute>
                  <Suspense fallback={<Loading />}>
                    <InboundGoods />
                  </Suspense>
                </ProtectedRoute>
              ),
            },
            {
              path: "outbound-dispatch",
              element: (
                <ProtectedRoute>
                  <Suspense fallback={<Loading />}>
                    <OutboundDispatch />
                  </Suspense>
                </ProtectedRoute>
              ),
            },
            {
              path: "order-management",
              element: (
                <ProtectedRoute>
                  <Suspense fallback={<Loading />}>
                    <OrderManagement />
                  </Suspense>
                </ProtectedRoute>
              ),
            },
            {
              path: "stock-movement",
              element: (
                <ProtectedRoute>
                  <Suspense fallback={<Loading />}>
                    <StockMovement />
                  </Suspense>
                </ProtectedRoute>
              ),
            },
            {
              path: "inventory-audit-report",
              element: (
                <ProtectedRoute>
                  <Suspense fallback={<Loading />}>
                    <InventoryAuditReport />
                  </Suspense>
                </ProtectedRoute>
              ),
            },
          ],
        },

        {
          path: "clients",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <Clients />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "asset-deshboard-1",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <AssetDeshboard1 />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "digital-twin",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <DigitalTwinModule />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "pipeline-map",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <PipelineMap />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "settings",
          element: (
            <Suspense fallback={<Loading />}>
              <Settings />
            </Suspense>
          ),
        },
        {
          path: "hod-dashboard",
          element: (
            <Suspense fallback={<Loading />}>
              <HODDashboardContent />
            </Suspense>
          ),
        },
        {
          path: "department-dashboard",
          element: (
            <Suspense fallback={<Loading />}>
              <DepartmentDashboardContent />
            </Suspense>
          ),
        },
        {
          path: "command-centre-dashboard",
          element: (
            <Suspense fallback={<Loading />}>
              <CommandCentreContent />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: "tanker-filling-flow",
      element: (
        <Suspense fallback={<Loading />}>
          <TankerFillingFlow />
        </Suspense>
      ),
    },
    {
      path: "login",
      element: (
        <Suspense fallback={<Loading />}>
          <Login />
        </Suspense>
      ),
    },
    {
      path: "update-password",
      element: (
        <Suspense fallback={<Loading />}>
          <UpdatePassword />
        </Suspense>
      ),
    },
    {
      path: "register",
      element: (
        <Suspense fallback={<Loading />}>
          <SignUp />
        </Suspense>
      ),
    },
    {
      path: "forget-password",
      element: (
        <Suspense fallback={<Loading />}>
          <ForgotPassword />
        </Suspense>
      ),
    },
    {
      path: "accept-invitation",
      element: (
        <Suspense fallback={<Loading />}>
          <SetupAccount />
        </Suspense>
      ),
    },
    {
      path: "tanker-booking",
      element: (
        <Suspense fallback={<Loading />}>
          <Booking />
        </Suspense>
      ),
    },
    {
      path: "booking-confirm",
      element: (
        <Suspense fallback={<Loading />}>
          <ThankYouCard />
        </Suspense>
      ),
    },
    {
      path: ":userType/privacy-policy",
      element: (
        <Suspense fallback={<Loading />}>
          <PrivacyPolicyPage />
        </Suspense>
      ),
    },
    {
      path: "*",
      element: (
        <Suspense fallback={<Loading />}>
          <NotFoundPage />
        </Suspense>
      ),
    },
  ]);

  return <RouterProvider router={Router} />;
};

export default AppRouter;